import type { LayerRaw } from '@tsyl/karonte/lib/types/layers'

import { LAYER_ATTRIBUTIONS } from '@/constants/layers/attributions'
import type { LayerInfo } from '@/types/LayerInfo'

const info: LayerInfo = {
  description: {
    'en-US': 'Global SRTM Landforms',
    'es-ES': 'Global SRTM Landforms',
    'pt-BR': 'Global SRTM Landforms',
    'fr-FR': 'Global SRTM Landforms'
  },
  credit: LAYER_ATTRIBUTIONS.conservationSciencePartners,
  sourceUrl: 'https://developers.google.com/earth-engine/datasets/catalog/CSP_ERGo_1_0_Global_SRTM_landforms'
}

const landformNorthAfrica: LayerRaw = {
  id: 'Landform',
  type: 'WMS',
  url: '/wfas-proxy/geoserver/wms',
  extraArcgisParameter: { copyright: info.credit },
  title: {
    'en-US': 'Landform',
    'pt-BR': 'Forma do terreno',
    'es-ES': 'Forma del terreno',
    'fr-FR': 'Forme du terrain'
  },
  zindex: 1,
  active: true,
  activeTarget: 'visibility',
  enabled: false,
  opacity: 0.5,
  customProperties: { group: 'reference', info },
  legend: {
    title: 'n/a'
  },
  sublayers: [
    {
      id: 'naf:NWAF_LANDFORM',
      visible: true,
      legend: {
        title: {
          'en-US': 'Landform',
          'pt-BR': 'Forma do terreno',
          'es-ES': 'Forma del terreno',
          'fr-FR': 'Forme du terrain'
        },
        items: [
          {
            text: {
              'en-US': 'Peak/ridge (warm)',
              'es-ES': 'Pico/cresta (cálido)',
              'pt-BR': 'Pico/cordilheira (quente)',
              'fr-FR': 'Pic/crête (chaud)'
            },
            color: '#141414',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Peak/ridge',
              'es-ES': 'Pico/cresta',
              'pt-BR': 'Pico/cordilheira',
              'fr-FR': 'Pic/crête'
            },
            color: '#383838',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Peak/ridge (cool)',
              'es-ES': 'Pico/cresta (fresco)',
              'pt-BR': 'Pico/cordilheira (fresco)',
              'fr-FR': 'Pic/crête (frais)'
            },
            color: '#808080',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Mountain/divide',
              'es-ES': 'Montaña/divisoria',
              'pt-BR': 'Montanha/divisor',
              'fr-FR': 'Montagne/ligne de partage'
            },
            color: '#EBEB8F',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Cliff',
              'es-ES': 'Acantilado',
              'pt-BR': 'Penhasco',
              'fr-FR': 'Falaise'
            },
            color: '#F7D311',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Upper slope (warm)',
              'es-ES': 'Pendiente superior (cálido)',
              'pt-BR': 'Inclinação superior (quente)',
              'fr-FR': 'Pente supérieure (chaud)'
            },
            color: '#AA0000',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Upper slope',
              'es-ES': 'Pendiente superior',
              'pt-BR': 'Inclinação superior',
              'fr-FR': 'Pente supérieure'
            },
            color: '#D89382',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Upper slope (cool)',
              'es-ES': 'Pendiente superior (fresco)',
              'pt-BR': 'Inclinação superior (fresco)',
              'fr-FR': 'Pente supérieure (frais)'
            },
            color: '#DDC9C9',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Upper slope (flat)',
              'es-ES': 'Pendiente superior (plano)',
              'pt-BR': 'Inclinação superior (plano)',
              'fr-FR': 'Pente supérieure (plat)'
            },
            color: '#DCCDCE',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Lower slope (warm)',
              'es-ES': 'Pendiente inferior (cálido)',
              'pt-BR': 'Inclinação inferior (quente)',
              'fr-FR': 'Pente inférieure (chaud)'
            },
            color: '#1C6330',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Lower slope',
              'es-ES': 'Pendiente inferior',
              'pt-BR': 'Inclinação inferior',
              'fr-FR': 'Pente inférieure'
            },
            color: '#68AA63',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Lower slope (cool)',
              'es-ES': 'Pendiente inferior (fresco)',
              'pt-BR': 'Inclinação inferior (fresco)',
              'fr-FR': 'Pente inférieure (frais)'
            },
            color: '#B5C98E',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Lower slope (flat)',
              'es-ES': 'Pendiente inferior (plano)',
              'pt-BR': 'Inclinação inferior (plano)',
              'fr-FR': 'Pente inférieure (plat)'
            },
            color: '#E1F0E5',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Valley',
              'es-ES': 'Valle',
              'pt-BR': 'Vale',
              'fr-FR': 'Vallée'
            },
            color: '#A975BA',
            icon: 'mdi-square'
          },
          {
            text: {
              'en-US': 'Valley (narrow)',
              'es-ES': 'Valle (estrecho)',
              'pt-BR': 'Vale (estreito)',
              'fr-FR': 'Vallée (étroit)'
            },
            color: '#6F198C',
            icon: 'mdi-square'
          }
        ]
      }
    }
  ],
  icon: 'mdi-barley'
}

export default landformNorthAfrica
